import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Avatar, Select, Tooltip } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import GetEmployeeSummaryApi from "../../api/performance/GetEmployeeSummaryApi";
import { SimpleLoader } from "../sub/Loaders";
import Theme from "../../theme/Theme";

const { Option } = Select;

const ROW_HEIGHT = 52;
const VISIBLE_ROWS = 5;

function getStatusColor(perf) {
  if (perf >= 70) return { color: "#87d068", label: "On Track" };
  if (perf >= 40) return { color: "#FAB324", label: "Off Track" };
  return { color: Theme().attentionCritical, label: "At Risk" };
}

function EmployeePerformanceSummary() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const positionState = useSelector((state) => state.positionState.value);

  useEffect(() => {
    fetchData();
  }, [perfCycleState]);

  async function fetchData() {
    if (!perfCycleState?.id || !positionState?.id) return;
    setLoading(true);
    const res = await GetEmployeeSummaryApi(positionState.id, perfCycleState.id);
    if (res?.success) setEmployees(res.data || []);
    setLoading(false);
  }

  const userOptions = useMemo(() => {
    const seen = new Set();
    return employees
      .filter((emp) => {
        const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim();
        if (!name || seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .map((emp) => ({
        label: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
        value: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
      }));
  }, [employees]);

  const filtered = useMemo(() => {
    if (!selectedUser) return employees;
    return employees.filter(
      (emp) =>
        `${emp.firstName || ""} ${emp.lastName || ""}`.trim() === selectedUser
    );
  }, [employees, selectedUser]);

  return (
    <div
      className="border rounded-md p-5 mb-6"
      style={{ borderColor: Theme().navBorder }}
    >
      <style>{`.black-border-select .ant-select-selector { border-color: #000 !important; } .black-border-select.ant-select-focused .ant-select-selector { border-color: #000 !important; box-shadow: none !important; }`}</style>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div
          className="text-base"
          style={{ color: Theme().textPrimary, fontWeight: 500 }}
        >
          Employee Performance Summary
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={selectedUser}
            onChange={setSelectedUser}
            placeholder="All employees"
            style={{ width: 150, borderColor: "#000" }}
            size="small"
            allowClear
            className="black-border-select"
          >
            {userOptions.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
          <Link
            to="/positions-performance"
            className="text-xs flex items-center gap-1"
            style={{ color: Theme().primary }}
          >
            View all <ArrowRightOutlined />
          </Link>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <SimpleLoader />
      ) : filtered.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-4">
          {selectedUser ? "No positions found for this employee" : "No employee data available"}
        </div>
      ) : (
        <div
          className="overflow-y-auto custom-scroll"
          style={{ maxHeight: ROW_HEIGHT * VISIBLE_ROWS }}
        >
          <div className="space-y-2">
            {filtered.map((emp, i) => {
              const { color, label } = getStatusColor(emp.avgPerformance);
              const initials = emp.firstName?.charAt(0)?.toUpperCase() || "?";
              const name = emp.firstName
                ? `${emp.firstName} ${emp.lastName || ""}`.trim()
                : "Unknown";

              return (
                <Link
                  key={`${emp.positionId}-${i}`}
                  to={`/positions-performance/${emp.positionId}`}
                  className="block text-black hover:text-black"
                >
                  <div
                    className="flex items-center gap-3 rounded px-2 py-1 hover:bg-gray-50 transition-colors"
                    style={{ minHeight: ROW_HEIGHT - 8 }}
                  >
                    {/* Rank */}
                    <div
                      className="text-xs w-4 text-right shrink-0"
                      style={{ color: Theme().textPrimary, opacity: 0.35 }}
                    >
                      {i + 1}
                    </div>

                    {/* Avatar */}
                    {emp.imageUrl ? (
                      <Avatar size={30} src={emp.imageUrl} />
                    ) : (
                      <Avatar size={30} style={{ backgroundColor: Theme().primary, fontSize: 13 }}>
                        {initials}
                      </Avatar>
                    )}

                    {/* Name + badges */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span
                          className="text-sm truncate"
                          style={{ color: Theme().textPrimary, fontWeight: 500 }}
                        >
                          {name}
                        </span>
                        {emp.hasParentPlans && (
                          <Tooltip title="Holds parent plans">
                            <span
                              className="px-1 rounded"
                              style={{
                                backgroundColor: "rgba(241,90,36,0.1)",
                                color: Theme().primary,
                                fontSize: 9,
                              }}
                            >
                              Parent
                            </span>
                          </Tooltip>
                        )}
                        {emp.hasCascadePlans && (
                          <Tooltip title="Assigned cascade plans">
                            <span
                              className="px-1 rounded"
                              style={{
                                backgroundColor: "rgba(135,208,104,0.12)",
                                color: "#52a832",
                                fontSize: 9,
                              }}
                            >
                              Cascade
                            </span>
                          </Tooltip>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 truncate">
                        {emp.designationName}
                      </div>
                    </div>

                    {/* Bar + % */}
                    <div className="flex items-center gap-2 shrink-0" style={{ width: 150 }}>
                      <div
                        className="flex-1 rounded-full overflow-hidden"
                        style={{ height: 5, backgroundColor: "rgba(0,0,0,0.07)" }}
                      >
                        <div
                          style={{
                            width: `${Math.min(emp.avgPerformance, 100)}%`,
                            height: "100%",
                            backgroundColor: color,
                            borderRadius: 9999,
                            transition: "width 0.4s ease",
                          }}
                        />
                      </div>
                      <div
                        className="text-xs font-medium w-10 text-right"
                        style={{ color }}
                      >
                        {emp.avgPerformance.toFixed(1)}%
                      </div>
                    </div>

                    {/* Status dot */}
                    <Tooltip title={`${label} · ${emp.planCount} plan${emp.planCount !== 1 ? "s" : ""}`}>
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: color }}
                      />
                    </Tooltip>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default EmployeePerformanceSummary;
